'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import styles from './PlatformIndexLogin.module.css';
import { getMe, login } from '@/lib/client/platformApi';
import { clearStoredSession } from '@/lib/client/platformSession';

const ROLE_PATHS = {
  admin: '/admin',
  examiner: '/examiner',
  candidate: '/candidate',
  supervisor: '/supervisor',
};

function getRolePath(role) {
  return ROLE_PATHS[role] || '/';
}

export default function PlatformIndexLogin() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function restoreSession() {
      try {
        const me = await getMe();
        if (!active) return;
        router.replace(getRolePath(me.role));
        return;
      } catch {
        clearStoredSession();
        if (active) setBooting(false);
      }
    }

    restoreSession();

    return () => {
      active = false;
    };
  }, [router]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      const data = await login(email.trim().toLowerCase(), password);
      router.replace(getRolePath(data.user?.role));
    } catch (err) {
      setError(err.message || 'No se pudo iniciar sesión');
      setLoading(false);
    }
  }

  if (booting) {
    return (
      <main className={styles.page}>
        <div className={styles.backgroundGrid} />
        <section className={styles.wrap}>
          <div className={styles.header}>
            <div className={styles.logo}>LOT</div>
            <h1>Restaurando sesión…</h1>
            <p>Validando acceso a la plataforma</p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      <div className={styles.backgroundGrid} />
      <section className={styles.wrap}>
        <header className={styles.header}>
          <div className={styles.logo}>LOT</div>
          <h1>LOT INTERNACIONAL — Acceso</h1>
          <p>Plataforma de evaluación y certificación ISO 17024</p>
        </header>

        <div className={styles.card}>
          <form onSubmit={handleSubmit}>
            <div className={styles.fieldGroup}>
              <label htmlFor="login-email">Correo electrónico</label>
              <input
                id="login-email"
                className={styles.fieldControl}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="correo@ejemplo.com"
                autoComplete="username"
                required
              />
            </div>

            <div className={styles.fieldGroup}>
              <label htmlFor="login-password">Contraseña</label>
              <div className={styles.passwordWrap}>
                <input
                  id="login-password"
                  className={styles.fieldControl}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  className={styles.passwordToggle}
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error ? <div className={styles.errorBox}>{error}</div> : null}

            <button className={styles.loginButton} type="submit" disabled={loading}>
              {loading ? 'Ingresando…' : 'Ingresar'}
            </button>
          </form>
        </div>

        <footer className={styles.footer}>
          Integridad del proceso de evaluación · LOT Internacional
        </footer>
      </section>
    </main>
  );
}
